import './App.scss';

import { Amplify } from 'aws-amplify';
import { LoadingOverlay, Snackbar, SubNavigation } from '@finpay-development/shared-components';
import { Grid, Box, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import jwt from 'jwt-decode';
import React, {useEffect, useRef, useMemo, useCallback} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from 'react-router-dom';

import {
  setPathName,
} from '@finpay-development/shared-platform/lib/esm/admin/state/users/admin-slice';
import RouteConfiguration, { RouteConfigProps } from './route-configuration';
import AuthenticationMonitor from './security/components/authentication-monitor';
import GuardedComponent from './security/components/guarded-component';
import SessionTimeout from '@finpay-development/shared-platform/lib/esm/security/components/session-timeout';
import { hideStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { checkIfUserIsAuthenticated, logUserOut, refreshToken } from '@finpay-development/shared-platform/lib/esm/security/state/user-thunk';
import FppHeader from './shared/components/header';
import { getConfiguration } from '@finpay-development/shared-platform/lib/esm/shared/configuration/config-settings';
import { DecodedIdToken } from './shared/model/decodedIdToken';
import { RootState } from './shared/state/root-reducer';
import { AppDispatch } from './shared/state/store';
import { PaymentHubButton } from '@finpay-development/shared-platform/lib/esm/payment-hub/components/payment-hub-button';
import { CallPatientButton } from './call-patient/call-patient-button';
import { usePaymentChannelSync } from '@finpay-development/shared-platform/lib/esm/payment-hub/hooks/use-payment-channel-sync';
import {getMetaData} from '@finpay-development/shared-platform/lib/esm/meta-data/state/meta-data-thunk';
import {clearFacilityPayers, clearVOB} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-slice";
import {clearPatient} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/vob-patient-slice";
import {clearEstimate} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/estimator-slice";
import {clearRiskAssessment} from "@finpay-development/shared-platform/lib/esm/admissions-advisor/state/risk-assessment-slice";
import {loadVobClassifications} from "@finpay-development/shared-platform/lib/esm/shared/state/vob-thunk";
import {configGetPayers} from "@finpay-development/shared-platform/lib/esm/admin-configuration/state/admin-configuration-thunk";
import {
  getClients
} from '@finpay-development/shared-platform/lib/esm/implementation-specialist/state/clients/implementation-clients-thunk';
import { clearPatientVobList, clearSelectedVob } from '@finpay-development/shared-platform/lib/esm/shared/state/vob-slice';
import { clearSavedEstimatesList, clearSelectedEstimate } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import {getClientWaivers, getRecentPatient} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import { getRateAsPayorMap } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import {clearEncounter} from '@finpay-development/shared-platform/lib/esm/patient/state/patient-slice';
import { cookieDomain } from '@finpay-development/shared-platform/lib/esm/security/services/cookie-domain';
import { CookieStorage } from 'aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import {
  UnauthenticatedAuthRouter
} from './security/components/unauthenticated-auth-router';
import {checkPagePermission} from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import { subNavItemsToIgnore, rejectURLAccessForReadOnly } from '@finpay-development/shared-platform/lib/esm/shared/constants/sub-nav-constants';
import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import { RolePageNames } from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import { RouteAnalyticsListener } from './shared/analytics/route-analytics-listener';
import {
  clearAnalyticsUserIdentity,
  setAnalyticsUserIdentity,
} from './shared/analytics/google-analytics';

// get the configuration based on current url dns name
const config = getConfiguration();


// Setup Amplify AWS User Pool Configuration
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.aws_userPoolId,
      userPoolClientId: config.aws_userPoolWebClientId,
      identityPoolId: ""
    },
  },
});


const isLocalhost = window.location.hostname.includes('localhost');

function PaymentChannelSynchronizer() {
  usePaymentChannelSync();
  return null;
}

cognitoUserPoolsTokenProvider.setKeyValueStorage(new CookieStorage({
  domain: cookieDomain,
  secure: !isLocalhost,
  expires: 1,
  sameSite: isLocalhost ? 'lax' : 'none'
}));

function App() {
  const dispatch = useDispatch<AppDispatch>();

  const hasCheckedUser = useRef(false);

  const stateFields = {
    userIsAuthenticated: useSelector((state: RootState) => state.userContext.userIsAuthenticated),
    userRolePages: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRolePages),
    subNav: useSelector((state: RootState) => state.userContext.userProfile.SubNav),
    isAccountHolderUser: useSelector((state: RootState) => state.userContext.userProfile.userRole.roleName === "Account Holder"),
    applicationStatus: useSelector((state: RootState) => state.userContext.applicationStatus),
    isLoading: useSelector((state: RootState) => state.userContext.isLoading),
    idToken: useSelector((state: RootState) => state.userContext.userProfile.tokenState.jwtIdToken),
    isPublicMode: useSelector((state: RootState) => state.adminContext.adminUserContext.isPublicMode),
    isPublicModeLoggedIn: useSelector((state: RootState) => state.adminContext.adminUserContext.isPublicModeLoggedIn),
    isSSO: useSelector((state: RootState) => state.userContext.isSSO),
    isSSOLoading: useSelector((state: RootState) => state.userContext.isSSOLoading),
    userRoleId: useSelector((state: RootState) => state.userContext.userProfile.userRole.userRoleId),
    showLogin: useSelector((state: RootState) => state.adminContext.adminUserContext.showLogin),
    pathName: useSelector((state: RootState) => state.adminContext.adminUserContext.pathName),
    allClients: useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient?.allClients || []),
    isLoadingClients: useSelector((state: RootState) => state.implementationContext?.implementationSpecialistClient?.isLoading || false),
    allowedClients: useSelector((state: RootState) => state.userContext.userProfile.allowedClients || []),
    userId: useSelector((state: RootState) => state.userContext.userProfile.userId),
    isInitialAuthCheckComplete: useSelector((state: RootState) => state.userContext.isInitialAuthCheckComplete),
  }

  const {
    userIsAuthenticated,
    userRolePages,
    subNav,
    applicationStatus,
    isLoading,
    idToken,
    isPublicModeLoggedIn,
    isSSO,
    isSSOLoading,
    userRoleId,
    pathName,
    allClients,
    isLoadingClients,
    userId,
    allowedClients,
    isInitialAuthCheckComplete,
  } = stateFields;

  // Show button if any client in the user's allowed client list has billingEngineVersion === 'V3'
  // allowedClients with clientId === 0 is wildcard (all clients)
  const shouldShowHubButton = useMemo(() => {
    if (isLoadingClients || !allowedClients?.length) {
      return false;
    }
    if (!allClients || allClients.length === 0) {
      return false;
    }
    const hasWildcard = allowedClients.some((c) => c.clientId === 0);
    if (hasWildcard) {
      return allClients.some((c) => c.billingEngineVersion === 'V3');
    }
    const allowedClientIds = allowedClients.map((c) => c.clientId);
    return allClients.some(
      (c) => allowedClientIds.includes(c.clientId) && c.billingEngineVersion === 'V3'
    );
  }, [allClients, isLoadingClients, allowedClients]);

  const theme = useTheme();
  // Mount only one hub button so Cypress / DOM never see mobile + desktop duplicates.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const path = window.location.pathname
    if(path.includes('forgot-password')){
      dispatch(logUserOut());
      dispatch(setPathName('forgot-password'))
    }
  }, [window.location.pathname]);



  useEffect(()=>{
    if(userIsAuthenticated && userRoleId !== 4){
      //fetching metadata in case browser is refreshed
      dispatch(getMetaData());
      dispatch(configGetPayers(-2));
      dispatch(getClients({
        includeFacility: true,
      }))
      dispatch(loadVobClassifications(-2));
      dispatch(getClientWaivers())
      dispatch(getRateAsPayorMap({paramId: -2}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dispatch, userIsAuthenticated])

  useEffect(() => {
    if (userIsAuthenticated && userId && userId > 0 && (cookieDomain.includes('localhost') || cookieDomain.includes('finpay.net'))) {
      dispatch(getRecentPatient(userId));
    }
  }, [dispatch, userIsAuthenticated, userId]);

  // though the token is automatically refreshed when it expires and an api call happens,
  // this function may have some performance benefits due to caching.
  useEffect(() => {
    if (idToken) {
      const tokenInfo: DecodedIdToken = jwt(idToken);
      const timeoutMs = tokenInfo.exp * 1000 - new Date().getTime() + 500;
      setTimeout(() => {
        dispatch(refreshToken());
      }, timeoutMs);
    }
  }, [idToken, dispatch]);

  // Set user_id + finpay_user_id user property in Google Analytics (opaque internal id only)
  useEffect(() => {
    if (userIsAuthenticated && userId > 0) {
      setAnalyticsUserIdentity(String(userId));
    } else if (!userIsAuthenticated) {
      clearAnalyticsUserIdentity();
    }
  }, [userIsAuthenticated, userId]);

  const routeComponents = (
    <Routes>
      {RouteConfiguration.map((route: RouteConfigProps) => {
        return (
            route.protectedPageName ? (
                <Route
                    path={route.path}
                    key={route.path}
                    element={
                      <GuardedComponent
                          exact={true}
                          key={route.path}
                          path={route.path}
                          component={route.component}
                          isAuthenticated={userIsAuthenticated}
                          hasAccess={checkPagePermission({
                                userRolePages,
                                rolePageName: route.protectedPageName!,
                                permitReadOnly: !rejectURLAccessForReadOnly[route.protectedPageName]
                              }
                          )}
                      />
                    }
                />
            ) : (
                <Route
                    key={route.path}
                    path={route.path}
                    element={<route.component/>}
                />
            )
        )
      })}
    </Routes>
  );

  // use in case the promise is empty string, to prevent errors.
  const routeComponentsWithoutStripe = (
    <Routes>
      {RouteConfiguration.map((route: RouteConfigProps) => (
        <Route
          key={route.path}
          path={route.path}
          element={<route.component/>}
        />
      ))}
    </Routes>
  );
  function handleCloseSnackbar() {
    dispatch(hideStatus());
  }

  const handleLogoutClick = useCallback(async () => {
    await dispatch(logUserOut());
    window.location.reload();
  }, [dispatch]);

  const shouldShowNav = () => {
    return (
      userIsAuthenticated &&
      !window.location.pathname.includes("sign-document") &&
      !window.location.pathname.includes("sign-doc")
    ); // client signing URL
  };

  useEffect(() => {
    if (!hasCheckedUser.current && !(userIsAuthenticated && isSSO)) {
      dispatch(checkIfUserIsAuthenticated());
    }
    if (userIsAuthenticated && isSSO) {
        hasCheckedUser.current = true;
    }
  }, [dispatch, userIsAuthenticated, isSSO]);

  function toggleLoading(comp : JSX.Element){
    if (isSSOLoading || !isInitialAuthCheckComplete) {
      return <LoadingOverlay whiteBackground={true}/>;
    }
    return comp;
  }

  const isNotAuthenticated =(<UnauthenticatedAuthRouter/>);

  const isAuthenticated = (
    <>
      <AuthenticationMonitor />
      <div className="page-container">
        <FppHeader
          showLinks={shouldShowNav()}
          callPatient={!isDesktop ? <CallPatientButton /> : undefined}
          paymentHub={!isDesktop && shouldShowHubButton ? (
            <AccessControl
                rolePageName={RolePageNames.PaymentHub}
                actionName=""
                strategy={{ type: 'pageOnlyWithEdit' }}
                renderNoAccess={() => null}
            >
              <PaymentHubButton variant="inline" />
            </AccessControl>
          ) : undefined}
        />
        {isLoading || isSSOLoading ? (
            <LoadingOverlay />
        ) : (
          <>
            {shouldShowNav() && (
              <Box sx={{ 
                width: '100%',
                paddingBottom: '4px',
                overflow: 'visible',
                position: 'relative'
              }}>
                <Box sx={{ 
                  width: '100%',
                  backgroundColor: 'white',
                  borderBottom: '3px solid rgba(0, 0, 0, 0.15)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  zIndex: 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  // TODO(sub-nav border): SubNavigation (shared-components) draws
                  // its own 3px bottom border, duplicating the strip's border
                  // above. In this flex layout the inner border would only span
                  // the tabs, so it is hidden here. Simplify by removing the
                  // border from SubNavigation in shared-components so the strip
                  // is the single border owner, then delete this override.
                  '& nav.sub-navigation': { borderBottom: 'none' },
                }}>
                  <SubNavigation
                    subNav={subNav}
                    subNavItemsToIgnore={subNavItemsToIgnore}
                    onNavClick={() => {
                      dispatch(clearPatient());
                      dispatch(clearEncounter());
                      dispatch(clearVOB());
                      dispatch(clearEstimate());
                      dispatch(clearFacilityPayers());
                      dispatch(clearRiskAssessment());
                      // Adding v3 clears to sub-nav
                      dispatch(clearSelectedVob());
                      dispatch(clearPatientVobList());
                      dispatch(clearSelectedEstimate());
                      dispatch(clearSavedEstimatesList());
                    }}
                  />
                  {/* Desktop only — mobile mounts buttons next to the hamburger. */}
                  {isDesktop && (
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pr: '2rem',
                      '@media (min-width: 960px)': { pr: '7rem' },
                      '&:empty': { display: 'none' },
                    }}>
                      <CallPatientButton />
                      {shouldShowHubButton && (
                        <AccessControl
                          rolePageName={RolePageNames.PaymentHub}
                          actionName=""
                          strategy={{ type: 'pageOnlyWithEdit' }}
                          renderNoAccess={() => null}
                        >
                          <PaymentHubButton variant="inline" />
                        </AccessControl>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            { /* prevents stripe initialization errors caused by empty string passed into 'stripe' prop */}
            {config.stripe_key ? (routeComponents) : (routeComponentsWithoutStripe)}
          </>
        )}
      </div>
    </>
  );

  return (
    <main>
    <div
      className={"page-container"}
      style={{ backgroundColor: "#F5F7F9" }}
    >
      <Router>
        <PaymentChannelSynchronizer />
        <RouteAnalyticsListener />
        <Grid container>
          {!userIsAuthenticated && (
            <>
            <Grid item xs={12}>
              <FppHeader showLinks={shouldShowNav()} />
            </Grid>
            </>
          )}
          {!isPublicModeLoggedIn && pathName !== 'forgot-password' && (
          <Grid item md={12} xs={12}>
            {toggleLoading(userIsAuthenticated ? isAuthenticated : isNotAuthenticated)}
              <Snackbar
                open={applicationStatus.showMessage}
                message={applicationStatus.message}
                type={applicationStatus.messageType}
                onClose={handleCloseSnackbar}
              />
              <SessionTimeout
                isAuthenticated={userIsAuthenticated}
                logOut={handleLogoutClick}
              />
          </Grid>
          )}

        </Grid>
      </Router>
    </div>
    </main>
  );
}
export default App;
